<div class="import-modal">
  <div class="modal-header">
    <div class="title-block">
      <mat-icon class="title-icon">cloud_upload</mat-icon>
      <h2>Import D&amp;T IND Resource File</h2>
    </div>
    <button mat-icon-button class="close-btn" (click)="cancel()" aria-label="Close">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <div class="modal-body">
    <p class="intro">
      Upload the latest .xlsx workbook to seed or refresh resources, projects, assignments,
      and rate-card periods. Re-runs are safe — rows that already exist on
      (employee, project, billing start, capex/opex) are skipped automatically.
    </p>

    <div class="file-picker-row">
      <label class="file-input-label" for="migration-file-input">
        <mat-icon>attach_file</mat-icon>
        <span>{{ selectedFile ? 'Replace file' : 'Choose .xlsx file' }}</span>
      </label>
      <input
        id="migration-file-input"
        type="file"
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        (change)="onFileChosen($event)"
        hidden
      />

      <div *ngIf="selectedFile" class="file-chip">
        <mat-icon class="chip-icon">description</mat-icon>
        <div class="chip-meta">
          <span class="chip-name">{{ selectedFile.name }}</span>
          <span class="chip-size">{{ formatBytes(selectedFile.size) }}</span>
        </div>
        <button mat-icon-button class="chip-clear" (click)="clearSelection()" aria-label="Remove">
          <mat-icon>close</mat-icon>
        </button>
      </div>
    </div>

    <div *ngIf="errorMessage" class="error-banner">
      <mat-icon>error_outline</mat-icon>
      <span>{{ errorMessage }}</span>
    </div>

    <ng-container *ngIf="uploading && job">
      <div class="progress-block">
        <div class="progress-header">
          <mat-icon class="spinning">autorenew</mat-icon>
          <span class="progress-status">{{ job.status }}</span>
          <span class="progress-count">
            {{ job.processed }} / {{ job.totalRowsRead || '…' }} rows
          </span>
        </div>
        <div class="progress-bar">
          <div class="progress-bar-fill" [style.width.%]="progressPercent"></div>
        </div>
        <div class="progress-counters">
          <span class="counter counter-good">Imported {{ job.imported }}</span>
          <span class="counter counter-neutral">Skipped {{ job.skipped }}</span>
          <span class="counter" [class.counter-bad]="job.errors > 0">Errors {{ job.errors }}</span>
          <span class="counter" [class.counter-warn]="job.warnings > 0">Warnings {{ job.warnings }}</span>
        </div>
        <p class="progress-hint">
          The job runs in the background — you can close this dialog and check the Resource Info
          page later. Reopening this modal won't show progress for a previous run.
        </p>
      </div>
    </ng-container>

    <ng-container *ngIf="isTerminal && job?.status === 'COMPLETED' && job?.report as report">
      <div class="report">
        <h3 class="report-title">Migration report</h3>
        <div class="stat-grid">
          <div class="stat-card">
            <span class="stat-label">Rows read</span>
            <span class="stat-value">{{ report.totalRowsRead }}</span>
          </div>
          <div class="stat-card stat-good">
            <span class="stat-label">Imported</span>
            <span class="stat-value">{{ report.imported }}</span>
          </div>
          <div class="stat-card stat-neutral">
            <span class="stat-label">Skipped (idempotent)</span>
            <span class="stat-value">{{ report.skippedIdempotent }}</span>
          </div>
          <div class="stat-card" [class.stat-bad]="report.errors > 0">
            <span class="stat-label">Errors</span>
            <span class="stat-value">{{ report.errors }}</span>
          </div>
          <div class="stat-card" [class.stat-warn]="report.warnings > 0">
            <span class="stat-label">Warnings</span>
            <span class="stat-value">{{ report.warnings }}</span>
          </div>
          <div class="stat-card">
            <span class="stat-label">Projects created</span>
            <span class="stat-value">{{ report.projectsCreated }}</span>
          </div>
          <div class="stat-card">
            <span class="stat-label">Resources created</span>
            <span class="stat-value">{{ report.resourcesCreated }}</span>
          </div>
          <div class="stat-card">
            <span class="stat-label">Ref. values created</span>
            <span class="stat-value">{{ report.referenceValuesCreated }}</span>
          </div>
          <div class="stat-card">
            <span class="stat-label">One-time entries</span>
            <span class="stat-value">{{ job?.oneTimeEntriesInserted ?? 0 }}</span>
          </div>
        </div>

        <ng-container *ngIf="errorList.length">
          <h4 class="list-heading">Errors</h4>
          <ul class="row-list errors-list">
            <li *ngFor="let e of errorList.slice(0, listLimit)">
              <span class="row-num">Row {{ e.sourceRowNumber }}</span>
              <span class="row-emp" *ngIf="e.employeeId">{{ e.employeeId }}</span>
              <span class="row-reason">{{ e.reason }}</span>
            </li>
          </ul>
          <p *ngIf="errorList.length > listLimit" class="more-line">
            …and {{ errorList.length - listLimit }} more.
          </p>
        </ng-container>

        <ng-container *ngIf="warningList.length">
          <h4 class="list-heading">Warnings</h4>
          <ul class="row-list warnings-list">
            <li *ngFor="let w of warningList.slice(0, listLimit)">
              <span class="row-num">Row {{ w.sourceRowNumber }}</span>
              <span class="row-emp" *ngIf="w.employeeId">{{ w.employeeId }}</span>
              <span class="row-reason">{{ w.reason }}</span>
            </li>
          </ul>
          <p *ngIf="warningList.length > listLimit" class="more-line">
            …and {{ warningList.length - listLimit }} more.
          </p>
        </ng-container>
      </div>
    </ng-container>

    <ng-container *ngIf="isTerminal && job?.status === 'FAILED'">
      <div class="failure-block">
        <mat-icon class="failure-icon">error</mat-icon>
        <div>
          <strong>Migration failed</strong>
          <p>{{ job?.errorMessage || 'See backend logs for details.' }}</p>
        </div>
      </div>
    </ng-container>
  </div>

  <div class="modal-footer">
    <button mat-stroked-button class="cancel-btn" type="button" (click)="cancel()" [disabled]="uploading && !isTerminal">
      {{ isTerminal ? 'Close' : 'Cancel' }}
    </button>
    <button
      *ngIf="!isTerminal"
      mat-flat-button
      class="upload-btn"
      type="button"
      (click)="upload()"
      [disabled]="!selectedFile || uploading"
    >
      {{ uploading ? (job?.status === 'RUNNING' ? 'Running…' : 'Starting…') : 'Upload' }}
    </button>
    <button
      *ngIf="isTerminal"
      mat-flat-button
      class="upload-btn"
      type="button"
      (click)="done()"
    >
      Done
    </button>
  </div>
</div>
